import { driver, type DriveStep, type Driver, type PopoverDOM } from "driver.js";
import { TOUR_MIN_WIDTH, TOUR_STOPS, tourStartsByItself, tourStops, type TourStop } from "@omnabase/shared";
import { api } from "./api";
import { followStage, settle, spanOver } from "./tourStage";

/** M11 round three (brief choice 8), redone after the founder's first test and his six steps (2026-10-07): the tour, walked with driver.js on
 *  the screens' `data-tour` marks. The stops and the rules for who gets which stop and when it starts by itself are shared's (pure); this
 *  module holds the one side-effecting runner: it opens each stop's place, waits until the place has settled (`lib/tourStage.ts`), then lets
 *  the highlight glide there; on Done, Skip or Esc, at any stop, it takes the popover's place before driver.js removes it (Omnalingo's
 *  `onDestroyStarted`), records the tour, gives the keyboard focus back and hands the place on for the goodbye dot. driver.js's own stylesheet
 *  is never loaded: the kit's `ob-tour` (`packages/ui/src/styles/tour.css`) carries the popover's layout and colours. Motion is the founder's
 *  exception: the glide (`animate`) of 500 ms, off for reduced motion; `smoothScroll: false`. */
export { TOUR_MIN_WIDTH, TOUR_STOPS, tourStartsByItself, tourStops };

/** the highlight's glide from one stop to the next: the kit's 150 ms plus the founder's 350 ms (2026-10-07: "the mask between steps is too
 *  fast"); the stage box still follows its place every frame, and the wait for a settled place before each stop is unchanged */
export const TOUR_GLIDE_MS = 500;

/** The app paths the stops live on (built by the caller from the workspace's slug) */
export type TourPlaces = { home: string; inbox: string; ticket: (n: number) => string; chat: string };

/** Where a stop must be opened before the popover can point at it, or null where it already is. Pure.
 *  - the welcome points at nothing and the rail is on every page;
 *  - the views column and the queue stand on an inbox list (never on Home, the column folded away while a ticket is open): the queue, unless
 *    a list is open;
 *  - the single ticket is the stop's ticket (the sample, else the queue's first); team chat is a room (#general unless one is open); the next
 *    steps are on Home. */
export function placeFor(stop: TourStop, pathname: string, places: TourPlaces, ticket: number | null): string | null {
  if (stop.id === "inbox") return /^\/[^/]+\/inbox\/[^/]+\/?$/.test(pathname) ? null : places.inbox;
  if (stop.id === "ticket") return ticket !== null && !pathname.endsWith(`/${ticket}`) ? places.ticket(ticket) : null;
  if (stop.id === "chat") return /^\/[^/]+\/chat\/.+/.test(pathname) ? null : places.chat;
  if (stop.id === "next") return pathname.replace(/\/$/, "") === places.home.replace(/\/$/, "") ? null : places.home;
  return null;
}

/** The ticket the single-ticket stop opens: the sample for its owner, else the first of the person's queue, else none (the stop is skipped) */
export const tourTicket = (sample: number | null, queue: readonly { number: number }[] | null | undefined): number | null => sample ?? queue?.[0]?.number ?? null;

/** Where the popover was when the tour ended (its centre, taken before driver.js removed it), for the goodbye dot; null when none was on screen */
export type TourEnd = { from: { x: number; y: number } | null };
export type TourRun = {
  stops: TourStop[]; /** the single-ticket stop's ticket (`tourTicket`) */ ticket: number | null; places: TourPlaces;
  /** moves the app (an app path) */ navigate: (to: string) => void;
  /** where the keyboard focus goes when the tour ends (where it was before) */ returnFocus: Element | null;
  /** after the tour ended, whichever way */ onEnd?: (end: TourEnd) => void;
};

let active: Driver | null = null;
export const tourRunning = () => active !== null;

/** driver.js's step for a stop: no element for the welcome (a centred dialog), else the stop's box over its marks */
function stepFor(s: TourStop, span: HTMLElement | undefined): DriveStep {
  const popover = { title: s.title, description: s.text, ...(s.nextLabel ? { nextBtnText: s.nextLabel } : {}) };
  return span ? { element: span, popover } : { popover };
}

/** Start the tour (nothing when one runs or there is no stop). Done, Skip and Esc all end it, at any stop, and record it as seen. */
export async function runTour(run: TourRun): Promise<void> {
  if (active || run.stops.length === 0) return;
  const stops = run.stops;
  const spans = new Map(stops.filter((s) => s.marks.length > 0).map((s) => [s.id, spanOver(s.id, s.marks)] as const));
  let busy = false; let ended = false; let glideUntil = 0; let popover: PopoverDOM | null = null;
  const d = driver({
    steps: stops.map((s) => stepFor(s, spans.get(s.id)?.el)),
    animate: !matchMedia("(prefers-reduced-motion: reduce)").matches, duration: TOUR_GLIDE_MS, smoothScroll: false, allowKeyboardControl: true, overlayClickBehavior: "none", overlayOpacity: 0.45, stagePadding: 6, stageRadius: 6,
    popoverClass: "ob-tour", showProgress: true, progressText: "{{current}} of {{total}}",
    nextBtnText: "Next", prevBtnText: "Back", doneBtnText: "Done", closeBtnLabel: "Skip the tour",
    onPopoverRender: (p) => { popover = p; dress(p); },
    onNextClick: () => void step(+1),
    onPrevClick: () => void step(-1),
    onDestroyStarted: () => end(), // Skip and Esc: driver.js asks before it removes anything, so the popover is still there to be measured
  });
  active = d;
  const unblock = tourKeys((dir) => void step(dir));
  const unfollow = followStage(d, () => performance.now() < glideUntil);
  const show = (i: number) => { glideUntil = performance.now() + TOUR_GLIDE_MS + 50; if (d.isActive()) d.moveTo(i); else d.drive(i); };

  /** open the stop's place, wait until it has settled; false when it never does */
  const reach = async (i: number) => {
    const to = placeFor(stops[i]!, window.location.pathname, run.places, run.ticket);
    if (to) { run.navigate(to); if (!(await waitFor(() => window.location.pathname === to.split("?")[0]))) return false; }
    return stops[i]!.marks.length === 0 || settle(stops[i]!.marks);
  };
  /** the next stop in a direction whose place can be reached (a missing one is passed over), or the end. While the page changes the popover
   *  is hidden and the highlight follows its old element; the highlight glides to the new place once it stands still. */
  const step = async (dir: 1 | -1) => {
    if (busy || ended) return;
    busy = true;
    try {
      let i = (d.getActiveIndex() ?? 0) + dir;
      if (i >= 0 && i < stops.length && popover) popover.wrapper.style.visibility = "hidden";
      while (i >= 0 && i < stops.length && !(await reach(i))) i += dir;
      if (ended) return;
      if (i >= stops.length) end();
      else if (i >= 0) show(i);
      else if (popover) popover.wrapper.style.visibility = "";
    } finally { busy = false; }
  };
  /** every end (Skip, Esc, Done): the popover's place first, while it is still in the page (Omnalingo's `onDestroyStarted`), then away */
  function end() {
    if (ended) return;
    ended = true; active = null;
    const from = popoverCentre(popover);
    unfollow();
    d.destroy();
    for (const s of spans.values()) s.remove();
    unblock();
    const back = run.returnFocus;
    if (back instanceof HTMLElement && back.isConnected) back.focus(); else (document.activeElement as HTMLElement | null)?.blur?.();
    void api.put("/api/v1/me/tour", {}).catch(() => undefined); // once per person; a failed mark only means the tour may come back
    run.onEnd?.({ from });
  }

  let first = 0;
  while (first < stops.length && !(await reach(first))) first++;
  if (first >= stops.length || ended) { ended = true; active = null; unfollow(); for (const s of spans.values()) s.remove(); unblock(); return; }
  show(first);
}

/** the centre of the popover on screen, or null */
function popoverCentre(p: PopoverDOM | null): { x: number; y: number } | null {
  if (!p?.wrapper.isConnected) return null;
  const r = p.wrapper.getBoundingClientRect();
  return r.width > 0 ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : null;
}

/** the kit's buttons on driver.js's popover; the close × becomes a worded "Skip" at the footer's start. driver.js focuses the first button
 *  right after this; the focus then moves on to Next, so Enter goes forward */
function dress(p: PopoverDOM) {
  queueMicrotask(() => p.nextButton.focus());
  p.closeButton.textContent = "Skip";
  p.closeButton.className = "driver-popover-close-btn ob-btn ob-btn--ghost ob-btn--sm ob-tour__skip";
  p.previousButton.classList.add("ob-btn", "ob-btn--secondary", "ob-btn--sm", "ob-tour__back");
  p.nextButton.classList.add("ob-btn", "ob-btn--primary", "ob-btn--sm", "ob-tour__next");
  p.footer.prepend(p.closeButton);
}

/** While the tour runs, the app's own keys stay quiet (Esc would close the open ticket, arrows move the list, "?" open Help): everything but
 *  Tab stops at the window on its way down. The buttons still answer Enter and Space (their default action), and driver.js reads Esc on
 *  key-up. The arrows are the runner's (on key-up, as driver.js would): driver.js drops an arrow pressed while its highlight glides, the
 *  runner takes it whenever it is not already moving to a stop. */
function tourKeys(onArrow: (dir: 1 | -1) => void): () => void {
  const down = (e: KeyboardEvent) => { if (e.key !== "Tab") e.stopImmediatePropagation(); };
  const up = (e: KeyboardEvent) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.stopImmediatePropagation();
    onArrow(e.key === "ArrowRight" ? 1 : -1);
  };
  window.addEventListener("keydown", down, true);
  window.addEventListener("keyup", up, true);
  return () => { window.removeEventListener("keydown", down, true); window.removeEventListener("keyup", up, true); };
}

/** true once `ok()` holds, polled every 50 ms; false after `ms` */
async function waitFor(ok: () => boolean, ms = 8000): Promise<boolean> {
  const until = Date.now() + ms;
  while (!ok()) { if (Date.now() > until) return false; await new Promise((r) => setTimeout(r, 50)); }
  return true;
}
